import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, X, Send, Plus, Check, Loader2 } from "lucide-react";
import { api, API } from "@/lib/api";

const VERKTOY_LABEL = {
  hent_status: "Henter status …",
  list_speakers: "Leser speaker-pipelinen …",
  list_partnere: "Leser partner-pipelinen …",
  list_saker: "Leser saker …",
  budsjett_sammendrag: "Leser budsjettet …",
  foreslaa_speakers: "Lager speaker-forslag …",
  legg_til_speakers: "Legger til speakers …",
  oppdater_speaker: "Oppdaterer speaker …",
  oppdater_partner: "Oppdaterer partner …",
  opprett_sak: "Oppretter sak …",
  list_dokumenter: "Leser dokumentlisten …",
  les_dokument: "Leser dokumentet …",
  skriv_dokument: "Skriver dokument …",
};

const CHIPS = [
  "Foreslå 10 relevante speakers",
  "Hvem bør jeg følge opp i dag?",
  "Status på partner-pipelinen",
  "Oppsummer budsjettet",
];

const fet = (s) =>
  String(s).split(/\*\*(.+?)\*\*/g).map((p, i) =>
    i % 2 ? <b key={i} className="font-semibold text-neutral-900">{p}</b> : p);

const Md = ({ tekst }) => (
  <div className="space-y-1">
    {String(tekst).split("\n").map((l, i) => {
      const li = l.match(/^\s*[-•*] (.*)/);
      if (li) return <p key={i} className="flex gap-1.5 leading-relaxed"><span className="text-neutral-300 shrink-0">•</span><span>{fet(li[1])}</span></p>;
      const h = l.match(/^#{1,3} (.*)/);
      if (h) return <p key={i} className="font-semibold text-neutral-900 mt-1">{fet(h[1])}</p>;
      if (!l.trim()) return <span key={i} className="block h-1" />;
      return <p key={i} className="leading-relaxed">{fet(l)}</p>;
    })}
  </div>
);

const ForslagKort = ({ forslag, meldingIdx }) => {
  const [valgte, setValgte] = useState(() => new Set(forslag.map((_, i) => i)));
  const [lagtTil, setLagtTil] = useState(false);
  const [jobber, setJobber] = useState(false);

  const toggle = (i) => setValgte((prev) => {
    const n = new Set(prev);
    if (n.has(i)) n.delete(i); else n.add(i);
    return n;
  });

  const leggTil = async () => {
    const utvalg = forslag.filter((_, i) => valgte.has(i));
    if (!utvalg.length) return;
    setJobber(true);
    let ok = 0, feilet = 0;
    for (const f of utvalg) {
      try {
        await api.post("/admin/os/speakers", {
          name: f.name, title: f.title || "", org: f.org || "",
          stage: "researching", notes: f.begrunnelse || "", actor: "AI-assistent",
        });
        ok += 1;
      } catch {
        feilet += 1;
      }
    }
    setJobber(false);
    setLagtTil(true);
    if (ok) toast.success(`${ok} speaker${ok > 1 ? "e" : ""} lagt til som Target`);
    if (feilet) toast.error(`${feilet} kunne ikke legges til (finnes trolig allerede)`);
  };

  return (
    <div className="mt-2.5 space-y-1.5" data-testid={`assistent-forslag-${meldingIdx}`}>
      {forslag.map((f, i) => (
        <button
          key={i}
          type="button"
          disabled={lagtTil}
          onClick={() => toggle(i)}
          className={`w-full text-left flex items-start gap-2.5 border rounded-xl px-3 py-2 transition-colors ${
            valgte.has(i) && !lagtTil ? "border-neutral-900 bg-white" : "border-[#E7E4DB] bg-white opacity-70"
          }`}
          data-testid={`forslag-velg-${meldingIdx}-${i}`}
        >
          <span className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
            valgte.has(i) ? "bg-neutral-900 border-neutral-900 text-white" : "border-[#D8D4C8] text-transparent"
          }`}>
            <Check size={10} />
          </span>
          <span className="min-w-0">
            <span className="block text-[12.5px] font-medium text-neutral-900">{f.name}</span>
            <span className="block text-[11px] text-neutral-400 truncate">{[f.title, f.org].filter(Boolean).join(" · ")}</span>
            {f.begrunnelse && <span className="block text-[11px] text-neutral-500 mt-0.5">{f.begrunnelse}</span>}
          </span>
        </button>
      ))}
      {!lagtTil ? (
        <button
          type="button"
          onClick={leggTil}
          disabled={jobber || valgte.size === 0}
          className="w-full inline-flex items-center justify-center gap-1.5 text-[12px] font-semibold bg-neutral-900 text-white rounded-xl px-3 py-2 hover:bg-neutral-700 transition-colors disabled:opacity-50"
          data-testid={`forslag-leggtil-btn-${meldingIdx}`}
        >
          {jobber ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
          Legg til valgte ({valgte.size}) som Target
        </button>
      ) : (
        <p className="text-[11px] text-emerald-600 font-medium text-center py-1" data-testid={`forslag-lagt-til-${meldingIdx}`}>
          Lagt til i pipelinen — se Speakers → Outreach
        </p>
      )}
    </div>
  );
};

export default function AssistentPanel() {
  const [aapen, setAapen] = useState(false);
  const [meldinger, setMeldinger] = useState([]);
  const [input, setInput] = useState("");
  const [jobber, setJobber] = useState(false);
  const [verktoy, setVerktoy] = useState(null);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("nas_assistent_session") || null);
  const bunn = useRef(null);
  const lastet = useRef(false);

  useEffect(() => {
    if (!aapen || lastet.current || !sessionId) return;
    lastet.current = true;
    api.get(`/admin/assistent/sessions/${sessionId}`)
      .then(({ data }) => setMeldinger(data.visning || []))
      .catch(() => { setSessionId(null); localStorage.removeItem("nas_assistent_session"); });
  }, [aapen, sessionId]);

  useEffect(() => {
    bunn.current?.scrollIntoView({ behavior: "smooth" });
  }, [meldinger, verktoy, aapen]);

  useEffect(() => {
    if (!aapen) return undefined;
    const esc = (e) => { if (e.key === "Escape") setAapen(false); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [aapen]);

  const nySamtale = () => {
    setMeldinger([]);
    setSessionId(null);
    localStorage.removeItem("nas_assistent_session");
    lastet.current = true;
  };

  const send = async (tekst) => {
    const melding = (tekst ?? input).trim();
    if (!melding || jobber) return;
    setInput("");
    setJobber(true);
    setMeldinger((prev) => [...prev, { role: "user", text: melding }, { role: "assistant", text: "", forslag: null }]);
    const oppdaterSiste = (fn) => setMeldinger((prev) => {
      const n = [...prev];
      n[n.length - 1] = fn(n[n.length - 1]);
      return n;
    });
    try {
      const res = await fetch(`${API}/admin/assistent/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId, message: melding }),
      });
      if (!res.ok || !res.body) throw new Error("stream");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const deler = buf.split("\n\n");
        buf = deler.pop();
        for (const d of deler) {
          const linje = d.trim();
          if (!linje.startsWith("data:")) continue;
          let ev;
          try { ev = JSON.parse(linje.slice(5)); } catch { continue; }
          if (ev.type === "delta") {
            setVerktoy(null);
            oppdaterSiste((m) => ({ ...m, text: (m.text || "") + ev.text }));
          } else if (ev.type === "verktoy") {
            setVerktoy(VERKTOY_LABEL[ev.navn] || "Jobber …");
          } else if (ev.type === "forslag") {
            oppdaterSiste((m) => ({ ...m, forslag: [...(m.forslag || []), ...ev.items] }));
          } else if (ev.type === "feil") {
            oppdaterSiste((m) => ({ ...m, text: (m.text || "") + (m.text ? "\n\n" : "") + ev.text }));
          } else if (ev.type === "ferdig" && ev.sessionId) {
            setSessionId(ev.sessionId);
            localStorage.setItem("nas_assistent_session", ev.sessionId);
          }
        }
      }
    } catch {
      oppdaterSiste((m) => ({ ...m, text: m.text || "Beklager — fikk ikke kontakt med assistenten. Prøv igjen." }));
    }
    setVerktoy(null);
    setJobber(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setAapen(true)}
        className="fixed bottom-5 right-5 z-40 w-12 h-12 rounded-full bg-neutral-900 text-white shadow-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
        title="AI-assistent"
        data-testid="assistent-aapne-btn"
      >
        <Sparkles size={19} />
      </button>

      {aapen && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="assistent-panel">
          <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={() => setAapen(false)} />
          <aside className="sak-drawer relative w-full max-w-[440px] h-full bg-white shadow-2xl border-l border-[#E7E4DB] flex flex-col">
            <div className="px-5 pt-4 pb-3 border-b border-neutral-100 shrink-0 flex items-center gap-2.5">
              <span className="w-8 h-8 rounded-full bg-neutral-900 text-white flex items-center justify-center shrink-0">
                <Sparkles size={14} />
              </span>
              <div className="mr-auto">
                <p className="text-[14px] font-semibold leading-tight">Assistent</p>
                <p className="text-[10px] text-neutral-400">GPT 5.6 · kan lese og handle i hele Event OS</p>
              </div>
              <button type="button" onClick={nySamtale} className="text-[11px] font-medium text-neutral-500 border border-[#E7E4DB] rounded-lg px-2 py-1 hover:border-neutral-400 hover:text-neutral-800 transition-colors" data-testid="assistent-ny-samtale">
                Ny samtale
              </button>
              <button type="button" onClick={() => setAapen(false)} className="p-1.5 text-neutral-400 hover:text-neutral-800 hover:bg-neutral-100 rounded-md transition-colors" data-testid="assistent-lukk">
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {meldinger.length === 0 && (
                <div className="pt-8" data-testid="assistent-tom">
                  <p className="text-[13px] text-neutral-500 leading-relaxed">
                    Hei! Jeg kjenner hele systemet — speakers, partnere, saker og budsjett. Jeg kan svare, oppdatere og foreslå. Prøv:
                  </p>
                  <div className="mt-4 space-y-1.5">
                    {CHIPS.map((c, i) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => send(c)}
                        className="block w-full text-left text-[12.5px] text-neutral-700 border border-[#E7E4DB] bg-white rounded-xl px-3 py-2 hover:border-neutral-400 transition-colors"
                        data-testid={`assistent-chip-${i}`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {meldinger.map((m, i) => (
                  <div key={i} data-testid={`assistent-melding-${i}`}>
                    {m.role === "user" ? (
                      <div className="flex justify-end">
                        <p className="max-w-[85%] text-[12.5px] leading-relaxed bg-neutral-900 text-white rounded-2xl rounded-br-md px-3.5 py-2">{m.text}</p>
                      </div>
                    ) : (
                      <div className="max-w-[95%] text-[12.5px] text-neutral-700">
                        {m.text ? <Md tekst={m.text} /> : (!m.forslag && i === meldinger.length - 1 && jobber && !verktoy && (
                          <span className="inline-flex gap-1 items-center text-neutral-300"><Loader2 size={12} className="animate-spin" /> tenker …</span>
                        ))}
                        {m.forslag && m.forslag.length > 0 && <ForslagKort forslag={m.forslag} meldingIdx={i} />}
                      </div>
                    )}
                  </div>
                ))}
                {verktoy && (
                  <p className="inline-flex items-center gap-1.5 text-[11px] text-neutral-400" data-testid="assistent-verktoy-status">
                    <Loader2 size={11} className="animate-spin" /> {verktoy}
                  </p>
                )}
              </div>
              <div ref={bunn} />
            </div>

            <div className="px-4 py-3 border-t border-neutral-100 shrink-0">
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  rows={1}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Spør om hva som helst — eller be meg gjøre noe …"
                  className="flex-1 resize-none text-[12.5px] border border-[#E7E4DB] rounded-xl px-3 py-2.5 bg-white focus:outline-none focus:border-neutral-400 transition-colors placeholder:text-neutral-300"
                  data-testid="assistent-input"
                />
                <button
                  type="button"
                  onClick={() => send()}
                  disabled={jobber || !input.trim()}
                  className="w-9 h-9 rounded-xl bg-neutral-900 text-white flex items-center justify-center hover:bg-neutral-700 transition-colors disabled:opacity-40 shrink-0"
                  data-testid="assistent-send"
                >
                  {jobber ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
